import React, { useContext, useEffect, useState, useCallback } from 'react';
import { AppContext } from '../../App';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import SuperAdminLayout from '../../components/SuperAdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Mail, CheckCircle, XCircle, MessageSquare,
  RefreshCw, Pencil, ArrowLeft,
  Users, Trophy, HelpCircle, Clock, Zap, Key,
  ExternalLink, Info, Copy
} from 'lucide-react';

// Helper to get auth config for axios requests
const getAuthConfig = () => {
  const token = localStorage.getItem('session_token');
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

// Predefined email trigger events with icons
const EVENT_ICONS = {
  player_invite: Users,
  welcome_email: Mail,
  draft_started: Zap,
  draft_turn: Clock,
  draft_completed: CheckCircle,
  bonus_question_available: HelpCircle,
  bonus_question_reminder: Clock,
  episode_scored: Trophy,
  password_reset: Key,
};

const EVENT_NAMES = {
  player_invite: 'Player Invite',
  welcome_email: 'Welcome Email',
  draft_started: 'Draft Started',
  draft_turn: 'Your Draft Turn',
  draft_completed: 'Draft Completed',
  bonus_question_available: 'Bonus Question Available',
  bonus_question_reminder: 'Bonus Question Reminder',
  episode_scored: 'Episode Scored',
  password_reset: 'Password Reset',
};

const CATEGORIES = {
  onboarding: { label: 'Onboarding', events: ['player_invite', 'welcome_email'], color: 'blue' },
  draft: { label: 'Draft', events: ['draft_started', 'draft_turn', 'draft_completed'], color: 'amber' },
  gameplay: { label: 'Gameplay', events: ['bonus_question_available', 'bonus_question_reminder', 'episode_scored'], color: 'purple' },
  account: { label: 'Account', events: ['password_reset'], color: 'slate' }
};

// SMS character limit (standard SMS is 160 chars, but we allow more for concatenation)
const SMS_CHAR_LIMIT = 320;

export default function SuperAdminEmailTriggers() {
  const { user, API } = useContext(AppContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [triggers, setTriggers] = useState([]);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedTrigger, setSelectedTrigger] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state - now includes SMS fields
  const [formData, setFormData] = useState({
    event_id: '',
    email_enabled: true,
    template_id: '',
    sms_enabled: false,
    sms_message: '',
    send_to: 'affected_player',
    delay_minutes: 0
  });

  const loadTriggers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/platform/email-triggers`, getAuthConfig());
      setTriggers(res.data || []);
    } catch (error) {
      console.error('Failed to load triggers:', error);
      toast.error('Failed to load notification triggers');
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    if (!user?.is_super_admin) {
      navigate('/home');
      return;
    }
    loadTriggers();
  }, [user, navigate, loadTriggers]);

  const handleEditTrigger = (trigger) => {
    setSelectedTrigger(trigger);
    setFormData({
      event_id: trigger.event_id,
      email_enabled: trigger.email_enabled ?? trigger.is_enabled ?? false,
      template_id: trigger.template_id || '',
      sms_enabled: trigger.sms_enabled ?? false,
      sms_message: trigger.sms_message || '',
      send_to: trigger.send_to || 'affected_player',
      delay_minutes: trigger.delay_minutes || 0
    });
    setShowEditDialog(true);
  };

  const handleSaveTrigger = async () => {
    try {
      setSaving(true);
      
      // Validate: if email enabled, need template
      if (formData.email_enabled && !formData.template_id) {
        toast.error('Please provide a SendGrid Template ID to enable email');
        setSaving(false);
        return;
      }
      
      // Validate: if SMS enabled, need message
      if (formData.sms_enabled && !formData.sms_message.trim()) {
        toast.error('Please provide an SMS message to enable SMS');
        setSaving(false);
        return;
      }
      
      await axios.patch(`${API}/platform/email-triggers/${formData.event_id}`, {
        email_enabled: formData.email_enabled,
        is_enabled: formData.email_enabled, // Keep backward compatibility
        template_id: formData.template_id,
        sms_enabled: formData.sms_enabled,
        sms_message: formData.sms_message,
        send_to: formData.send_to,
        delay_minutes: formData.delay_minutes
      }, getAuthConfig());
      
      setTriggers(prev => prev.map(t => 
        t.event_id === formData.event_id 
          ? { ...t, ...formData, is_enabled: formData.email_enabled, variables: t.variables } 
          : t
      ));
      
      toast.success('Notification trigger saved');
      setShowEditDialog(false);
    } catch (error) {
      toast.error('Failed to save trigger');
    } finally {
      setSaving(false);
    }
  };

  const getTriggerByEventId = (eventId) => {
    return triggers.find(t => t.event_id === eventId) || { 
      event_id: eventId, 
      email_enabled: false,
      is_enabled: false, 
      template_id: '',
      sms_enabled: false,
      sms_message: '',
      variables: { variables: [] }
    };
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };
  
  const insertVariable = (varName) => {
    const variable = `{{${varName}}}`;
    setFormData(prev => ({
      ...prev,
      sms_message: prev.sms_message + variable
    }));
    toast.success(`Added ${variable}`);
  };

  if (loading) {
    return (
      <SuperAdminLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout>
      <div className="max-w-4xl mx-auto space-y-4" data-testid="notification-triggers-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 mb-2">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/super-admin/email">
              <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </Link>
            <div>
              <h1 className="text-lg lg:text-2xl font-bold text-white">Notification Triggers</h1>
              <p className="text-xs lg:text-sm text-zinc-400">Configure Email & SMS notifications</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={loadTriggers} data-testid="refresh-triggers-btn" className="flex-shrink-0">
            <RefreshCw className={`w-4 h-4 lg:mr-1 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden lg:inline">Refresh</span>
          </Button>
        </div>

        {/* Info Banner */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-3">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-zinc-400 mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                <p className="text-zinc-300 text-sm">
                  Configure both <span className="text-violet-400 font-medium">Email</span> and <span className="text-sky-400 font-medium">SMS</span> for each trigger. Users can opt out of each channel separately.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Triggers by Category */}
        {Object.entries(CATEGORIES).map(([categoryId, category]) => (
          <div key={categoryId} className="space-y-3">
            <h2 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider flex items-center gap-2 px-1">
              {category.label}
            </h2>
            
            <div className="space-y-2">
              {category.events.map(eventId => {
                const Icon = EVENT_ICONS[eventId] || Mail;
                const trigger = getTriggerByEventId(eventId);
                const hasEmail = !!trigger.template_id;
                const hasSms = !!trigger.sms_message;
                const emailActive = (trigger.email_enabled ?? trigger.is_enabled) && hasEmail;
                const smsActive = trigger.sms_enabled && hasSms;
                
                return (
                  <Card 
                    key={eventId} 
                    className="bg-zinc-900/60 border-zinc-800/50 hover:bg-zinc-900 transition-colors cursor-pointer"
                    onClick={() => handleEditTrigger(trigger)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            (emailActive || smsActive) ? 'bg-emerald-500/15' : 'bg-zinc-800'
                          }`}>
                            <Icon className={`w-5 h-5 ${
                              (emailActive || smsActive) ? 'text-emerald-400' : 'text-zinc-400'
                            }`} />
                          </div>
                          <div>
                            <h3 className="font-medium text-white text-sm">{EVENT_NAMES[eventId]}</h3>
                            <p className="text-xs text-zinc-500">
                              {trigger.variables?.description || 'Configure this notification'}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {/* Email Status */}
                          <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs ${
                            emailActive 
                              ? 'bg-violet-500/20 text-violet-400' 
                              : hasEmail 
                                ? 'bg-zinc-800 text-zinc-400'
                                : 'bg-zinc-900 text-zinc-500'
                          }`}>
                            <Mail className="w-3 h-3" />
                            <span className="hidden sm:inline">{emailActive ? 'On' : hasEmail ? 'Off' : '—'}</span>
                          </div>
                          
                          {/* SMS Status */}
                          <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs ${
                            smsActive 
                              ? 'bg-sky-500/20 text-sky-400' 
                              : hasSms 
                                ? 'bg-zinc-800 text-zinc-400'
                                : 'bg-zinc-900 text-zinc-500'
                          }`}>
                            <MessageSquare className="w-3 h-3" />
                            <span className="hidden sm:inline">{smsActive ? 'On' : hasSms ? 'Off' : '—'}</span>
                          </div>
                          
                          <Pencil className="w-4 h-4 text-zinc-500 ml-2" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        ))}

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="bg-zinc-950 border-zinc-800 max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                Configure {EVENT_NAMES[formData.event_id]}
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                {selectedTrigger?.variables?.description}
              </DialogDescription>
            </DialogHeader>
            
            <Tabs defaultValue="email" className="mt-4">
              <TabsList className="bg-zinc-900 border border-zinc-800 w-full">
                <TabsTrigger value="email" className="flex-1 data-[state=active]:bg-violet-600">
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </TabsTrigger>
                <TabsTrigger value="sms" className="flex-1 data-[state=active]:bg-sky-600">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  SMS
                </TabsTrigger>
                <TabsTrigger value="settings" className="flex-1 data-[state=active]:bg-zinc-700">
                  Settings
                </TabsTrigger>
              </TabsList>
              
              {/* Email Tab */}
              <TabsContent value="email" className="space-y-4 mt-4">
                <div className="flex items-center justify-between p-3 bg-zinc-900 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-violet-400" />
                    <div>
                      <Label className="text-white">Email Notifications</Label>
                      <p className="text-xs text-zinc-400">Send via SendGrid</p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.email_enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, email_enabled: checked })}
                    disabled={!formData.template_id}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-300">SendGrid Template ID</Label>
                  <Input
                    value={formData.template_id}
                    onChange={(e) => setFormData({ ...formData, template_id: e.target.value })}
                    placeholder="d-xxxxxxxxxxxxxxxxxxxxxxxx"
                    className="bg-zinc-900 border-zinc-800 text-white font-mono"
                  />
                  <a 
                    href="https://mc.sendgrid.com/dynamic-templates" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-violet-400 hover:text-violet-300 text-xs"
                  >
                    Open SendGrid Dynamic Templates
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </TabsContent>
              
              {/* SMS Tab */}
              <TabsContent value="sms" className="space-y-4 mt-4">
                <div className="flex items-center justify-between p-3 bg-zinc-900 rounded-lg">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-5 h-5 text-sky-400" />
                    <div>
                      <Label className="text-white">SMS Notifications</Label>
                      <p className="text-xs text-zinc-400">Send via Twilio</p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.sms_enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, sms_enabled: checked })}
                    disabled={!formData.sms_message.trim()}
                  />
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-zinc-300">SMS Message</Label>
                    <span className={`text-xs ${
                      formData.sms_message.length > SMS_CHAR_LIMIT ? 'text-red-400' : 'text-zinc-500'
                    }`}>
                      {formData.sms_message.length}/{SMS_CHAR_LIMIT}
                    </span>
                  </div>
                  <Textarea
                    value={formData.sms_message}
                    onChange={(e) => setFormData({ ...formData, sms_message: e.target.value })}
                    placeholder="Hey {{player_name}}! You've been invited to {{league_name}}. Join now: {{invite_link}}"
                    className="bg-zinc-900 border-zinc-800 text-white min-h-24 font-mono text-sm"
                    maxLength={SMS_CHAR_LIMIT + 50}
                  />
                  <p className="text-xs text-zinc-500">
                    Use variables like {'{{player_name}}'} - click below to insert
                  </p>
                </div>
                
                {/* Quick Variable Insert */}
                {selectedTrigger?.variables?.variables?.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-zinc-400 text-xs">Insert Variable</Label>
                    <div className="flex flex-wrap gap-1">
                      {selectedTrigger.variables.variables.map((v) => (
                        <Button
                          key={v.name}
                          variant="outline"
                          size="sm"
                          onClick={() => insertVariable(v.name)}
                          className="text-xs bg-zinc-900 border-zinc-800 text-sky-400 hover:bg-sky-900/30"
                        >
                          {`{{${v.name}}}`}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>
              
              {/* Settings Tab */}
              <TabsContent value="settings" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Send To</Label>
                  <Select
                    value={formData.send_to}
                    onValueChange={(value) => setFormData({ ...formData, send_to: value })}
                  >
                    <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="affected_player">Affected Player Only</SelectItem>
                      <SelectItem value="all_players">All Players in League</SelectItem>
                      <SelectItem value="all_players_in_season">All Players in Season</SelectItem>
                      <SelectItem value="admins">League Admins Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-300">Delay (minutes)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.delay_minutes}
                    onChange={(e) => setFormData({ ...formData, delay_minutes: parseInt(e.target.value) || 0 })}
                    className="bg-zinc-900 border-zinc-800 text-white"
                  />
                  <p className="text-xs text-zinc-500">Set to 0 for immediate delivery</p>
                </div>
                
                {/* Available Variables Reference */}
                {selectedTrigger?.variables?.variables?.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-zinc-400 text-xs">Available Variables</Label>
                    <div className="bg-zinc-900 rounded-lg p-3 border border-zinc-800 space-y-2 max-h-40 overflow-y-auto">
                      {selectedTrigger.variables.variables.map((v) => (
                        <div key={v.name} className="flex items-center justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <code className="text-emerald-400 text-xs font-mono">
                              {`{{${v.name}}}`}
                            </code>
                            <p className="text-xs text-zinc-500 truncate">{v.description}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(`{{${v.name}}}`)}
                            className="text-zinc-500 hover:text-white flex-shrink-0 h-6 w-6 p-0"
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>
            
            <DialogFooter className="mt-6">
              <Button variant="ghost" onClick={() => setShowEditDialog(false)} className="text-zinc-400">
                Cancel
              </Button>
              <Button 
                onClick={handleSaveTrigger}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </SuperAdminLayout>
  );
}
